/**
 * AiRunModal — AI 를 부르기 직전에 한 번 멈추고, 사람의 지시를 받아 보내는 창.
 *
 * **새 관문이 아니다.** 이 저장소는 AI 를 부르는 자리 대부분에서 이미 window.confirm 으로
 * 한 번 묻고 있었다(비용·되돌릴 수 없음·대상 건수). 그 문구를 이 창의 본문으로 옮기고
 * 아래에 지시 칸을 단다 — 클릭 수는 늘지 않고, 서식이 없어 길게 쓰지 못하던 안내가 제자리를 찾는다.
 * 그러니 부르는 쪽은 **기존 확인창을 지우고 그 문구를 summary·warning 으로 넘겨야** 한다.
 * 둘 다 두면 창이 두 번 뜬다.
 *
 * ── 지킬 것 넷 ──
 * ① **지시는 선택이다.** 비우고 보낼 수 있고, 비면 onConfirm 이 undefined 를 받는다 —
 *    호출부는 그 값을 그대로 API 에 넘기고, API 계층이 「없으면 키를 싣지 않는다」를 지킨다.
 * ② **열 때마다 비운다.** 지난 요청의 지시가 남아 다른 대상에 실리면 조용히 엉뚱한 것이 만들어진다.
 * ③ **보내는 중에는 닫히지 않는다.** 배경 클릭은 항상 막고, ESC 와 [×]는 진행 중에만 막는다 —
 *    응답을 못 받고 닫히면 화면이 무슨 일이 났는지 말할 수 없다(FigureModal 과 같은 규칙).
 * ④ **모달 위에 뜰 수 있다.** 그때 부모는 반드시 `closeOnEsc={!aiOpen}` 으로 자기 ESC 를 꺼야 한다 —
 *    공용 Modal 이 document 에 전역 ESC 를 달아, 안 끄면 한 번에 둘 다 닫힌다.
 *    z 값의 출처는 src/styles/z-index.css 의 --z-modal-ai-run 이다(Modal 이 숫자를 받아
 *    인라인으로 쓰므로 CSS 변수를 그대로 넘기지 못한다 — 두 값을 함께 고쳐야 한다).
 *
 * ── `options` (2026-08-31) ──
 * 지시 칸 **위**에 호출부가 원하는 선택칸을 끼우는 자리. 지금 쓰는 곳은 그림 변환 모드 하나다
 * (계약 §9-4 — ProblemViewTab 의 [선 그림으로 바꾸기]).
 *
 * 이 창은 **그것이 무엇인지 알지 않는다.** 받아서 그리기만 하고, 값도 초기화도 호출부가 쥔다 —
 * 그래야 나머지 다섯 호출부가 새 조건을 읽지 않아도 된다. 종전에 그림 재요청 창(FigureReconvertModal)이
 * 따로 있는 이유가 「원본 이미지 올리기」라는 **동작**이 붙어서였는데, 이쪽은 그리기만 하는 자리라
 * 창을 하나 더 만들 이유가 없다.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from './Modal';
import { Button } from './Button';
import AiPromptInput from './AiPromptInput';

export default function AiRunModal({
  isOpen, title, summary, warning = null, options = null, promptLabel = 'AI 에 보낼 지시(선택)',
  placeholder = '', rows = 3, confirmText = '보내기', busy = false, onConfirm, onClose,
  showOptionPriority = false, confirmDisabled = false,
}) {
  const [prompt, setPrompt] = useState('');
  // 열 때마다 비운다(②). isOpen 이 false 로 갈 때가 아니라 true 로 올 때 비우는 이유는,
  // 닫히는 도중에 값이 사라지면 사라지는 창에서 글자가 깜빡이기 때문이다.
  useEffect(() => { if (isOpen) setPrompt(''); }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!busy) onClose(); }}
      title={title}
      size="md"
      closeOnBackdropClick={false}
      closeOnEsc={!busy}
      showCloseButton={!busy}
      zIndex={9999986}
    >
      <div className="space-y-3">
        {summary && <div className="text-sm leading-relaxed text-gray-700">{summary}</div>}
        {warning && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-800">
            {warning}
          </div>
        )}
        {/* 호출부가 끼운 선택칸 — 무엇인지 알지 않고 그리기만 한다(머리 주석 `options`). */}
        {options}
        {/* 「옵션·규칙이 지시를 이긴다」 안내는 그대로 흘려보내기만 한다 — 이 창은 옵션을 알지 못하고,
            켤지 말지는 옵션을 가진 호출부가 정한다(AiPromptInput 의 showOptionPriority 주석). */}
        <AiPromptInput
          label={promptLabel}
          value={prompt}
          onChange={setPrompt}
          rows={rows}
          placeholder={placeholder}
          disabled={busy}
          showOptionPriority={showOptionPriority}
        />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose} disabled={busy}>취소</Button>
          {/* 비워 두고 눌러도 된다 — 그때는 지금까지와 똑같은 요청이 나간다 */}
          <Button
            size="sm"
            onClick={() => onConfirm(prompt.trim() ? prompt : undefined)}
            loading={busy}
            disabled={busy || confirmDisabled}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

AiRunModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  summary: PropTypes.node,          // 지금 window.confirm 이 띄우던 안내를 그대로 옮긴다
  warning: PropTypes.node,          // 되돌릴 수 없음·미저장 등 — 있을 때만 노란 띠
  options: PropTypes.node,          // 지시 칸 위에 끼울 선택칸. 값·초기화는 호출부가 쥔다
  promptLabel: PropTypes.string,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  confirmText: PropTypes.string,
  busy: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired, // (userPrompt|undefined) — 비웠으면 undefined 다
  onClose: PropTypes.func.isRequired,
  showOptionPriority: PropTypes.bool,   // 난이도·정답 유형 같은 옵션을 함께 보내는 자리에서만 true
  // options 칸의 선택이 보낼 수 없는 상태일 때(예: 분석 대상을 하나도 안 고름) 호출부가 켠다.
  // 왜 막혔는지는 options 칸이 말한다 — 버튼만 잠기고 이유가 없으면 막다른 창이다.
  confirmDisabled: PropTypes.bool,
};
